import React, { useEffect, useState } from "react";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../../../firebase";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { Field, Fieldset, FieldGroup, Label, ErrorMessage } from "../../../design/fieldset";
import { useSupervisor } from "../../../contexts/SupervisorContext";
import { useAccount } from "../../../contexts/AccountContext";
import { Supervisor, storagePaths } from "@visitproof/types";
import { Timestamp } from "firebase/firestore";
import ProfileImageUpload from "../../../components/ProfileImageUpload";
import { getInitials } from "../../../utils/string";

interface Props {
  supervisor: Supervisor;
}

interface Errors {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  saveError?: string;
}

const EditSupervisorButton: React.FC<Props> = ({ supervisor }) => {
  const { updateSupervisor } = useSupervisor();
  const { account } = useAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [firstName, setFirstName] = useState<string>(
    supervisor.firstName ?? "",
  );
  const [lastName, setLastName] = useState<string>(supervisor.lastName ?? "");
  const [email, setEmail] = useState<string>(supervisor.email ?? "");
  const [phoneNumber, setPhoneNumber] = useState<string>(supervisor.phoneNumber ?? "");
  const [dateOfEmployment, setDateOfEmployment] = useState<string>(
    supervisor.dateOfEmployment?.toDate().toISOString().split("T")[0] ?? "",
  );

  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (isOpen) {
      setFirstName(supervisor.firstName ?? "");
      setLastName(supervisor.lastName ?? "");
      setEmail(supervisor.email ?? "");
      setPhoneNumber(supervisor.phoneNumber ?? "");
      setDateOfEmployment(
        supervisor.dateOfEmployment?.toDate().toISOString().split("T")[0] ?? "",
      );
      setFile(null);
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Errors = {};
    if (!firstName.trim()) newErrors.firstName = "First name is required.";
    if (!lastName.trim()) newErrors.lastName = "Last name is required.";
    if (!phoneNumber.trim()) newErrors.phoneNumber = "Phone number is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsUploading(true);

    try {
      let data: Partial<Supervisor> = {
        firstName,
        lastName,
        email,
        phoneNumber,
      };
      if (dateOfEmployment) {
        data = { ...data, dateOfEmployment: Timestamp.fromDate(new Date(dateOfEmployment)) };
      }
      if (file) {
        const path = account?.id
          ? storagePaths.supervisorAvatar(account.id, supervisor.id, file.name)
          : `supervisors/${supervisor.id}/${file.name}`;
        const fileRef = ref(storage, path);
        await uploadBytes(fileRef, file);
        const avatarUrl = await getDownloadURL(fileRef);
        data = { ...data, avatarUrl };
      }
      await updateSupervisor(supervisor.id, data);
      clearForm();
    } catch (error) {
      setErrors({ saveError: "Failed to save. Please try again later." });
    }

    setIsUploading(false);
  };

  const clearForm = () => {
    setIsOpen(false);
    setFile(null);
    setErrors({});
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Edit
      </Button>
      <Dialog open={isOpen} onClose={clearForm}>
        <DialogTitle>Edit Monitor</DialogTitle>
        <DialogBody>
          {errors.saveError && <ErrorMessage>{errors.saveError}</ErrorMessage>}

          <Fieldset>
            {/* Profile Image */}
            <div className="flex justify-center mb-6">
              <ProfileImageUpload
                currentImageUrl={supervisor.avatarUrl}
                initials={getInitials(`${firstName} ${lastName}`)}
                onFileSelected={setFile}
                size="lg"
                disabled={isUploading}
              />
            </div>

            <FieldGroup>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>First Name <span className="text-red-500">*</span></Label>
                  <Input
                    type="text"
                    value={firstName}
                    placeholder="First Name"
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
                </Field>
                <Field>
                  <Label>Last Name <span className="text-red-500">*</span></Label>
                  <Input
                    type="text"
                    value={lastName}
                    placeholder="Last Name"
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
                </Field>
              </div>
            </FieldGroup>

            <FieldGroup>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    placeholder="Email"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>Phone Number <span className="text-red-500">*</span></Label>
                  <Input
                    type="tel"
                    value={phoneNumber}
                    placeholder="Phone Number"
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                  {errors.phoneNumber && <ErrorMessage>{errors.phoneNumber}</ErrorMessage>}
                </Field>
              </div>
            </FieldGroup>

            <FieldGroup>
              <Field>
                <Label>Date of Employment</Label>
                <Input
                  type="date"
                  value={dateOfEmployment}
                  onChange={(e) => setDateOfEmployment(e.target.value)}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isUploading} onClick={handleSubmit}>
            {isUploading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditSupervisorButton;
